<template>
  <Drawer
    v-model:visible="visible"
    position="left"
    :style="{ width: drawerWidth }"
    :showHeader="false"
  >
    <div v-if="lead" class="flex flex-col h-full">
      <!-- Header -->
      <div class="flex-shrink-0 pb-3 border-b border-gray-100">
        <div class="flex items-start justify-between gap-2">
          <div class="min-w-0">
            <h2 class="font-bold text-gray-900 text-lg truncate">{{ lead.businessName }}</h2>
            <div class="flex items-center gap-2 text-xs text-gray-500 mt-0.5 flex-wrap">
              <span v-if="lead.contactName">{{ lead.contactName }}</span>
              <span v-if="lead.phone" dir="ltr" class="font-mono">{{ lead.phone }}</span>
            </div>
          </div>
          <Button icon="pi pi-times" text rounded size="small" class="!text-gray-400" @click="visible = false" />
        </div>

        <div class="flex flex-wrap items-center gap-1.5 mt-2">
          <span
            class="flex items-center gap-1 px-2 h-6 rounded-full border text-[11px] font-medium"
            :class="STAGE_CHIP_CLASSES[lead.stage]"
          >
            <i :class="['pi', STAGE_ICONS[lead.stage], 'text-[9px]']" />
            {{ STAGE_LABELS[lead.stage] }}
          </span>
          <Tag v-if="lead.source" :value="SOURCE_LABELS[lead.source]" severity="info" class="!text-[10px]" />
          <Tag v-if="lead.isTestBusiness" value="בדיקה" severity="warn" class="!text-[10px]" />
          <span
            v-if="stale"
            class="inline-flex items-center gap-1 text-[11px] text-red-600 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full font-medium"
          >
            <i class="pi pi-exclamation-triangle text-[10px]" />
            {{ staleLabel }}
          </span>
        </div>

        <div v-if="lead.phone" class="flex gap-2 mt-3">
          <a
            :href="`tel:${toDialable(lead.phone)}`"
            class="flex-1 flex items-center justify-center gap-2 h-10 rounded-xl bg-blue-600 text-white text-sm font-medium"
          >
            <i class="pi pi-phone text-xs" /> חייג
          </a>
          <a
            :href="`https://wa.me/${toWhatsappNumber(lead.phone)}`"
            target="_blank"
            rel="noopener"
            class="flex-1 flex items-center justify-center gap-2 h-10 rounded-xl bg-green-600 text-white text-sm font-medium"
          >
            <i class="pi pi-whatsapp text-xs" /> וואטסאפ
          </a>
          <Button
            icon="pi pi-pencil"
            label="סכם"
            class="!h-10 !rounded-xl !bg-gradient-to-r !from-purple-600 !to-indigo-600 !text-white !border-0 !text-sm"
            @click="emit('log', lead)"
          />
        </div>
      </div>

      <div class="flex-1 overflow-y-auto py-4 space-y-4">
        <!-- Registered business -->
        <div v-if="lead.business" class="space-y-2">
          <RouterLink
            :to="{ name: PlatformRouteNames.BusinessView, params: { bid: lead.business.bid } }"
            class="block bg-gradient-to-l from-primary-50 to-white border border-primary-100 rounded-2xl p-3 hover:border-primary-300 transition-colors"
          >
            <div class="flex items-center justify-between gap-2">
              <div class="flex items-center gap-2 min-w-0">
                <i class="pi pi-building text-primary-500 text-sm" />
                <span class="font-semibold text-gray-900 text-sm truncate">{{ lead.business.title }}</span>
              </div>
              <i class="pi pi-chevron-left text-primary-400 text-xs" />
            </div>
            <div class="grid grid-cols-4 gap-2 mt-3">
              <div class="text-center">
                <div class="text-lg font-bold text-gray-900">{{ lead.business.appointmentsTotal }}</div>
                <div class="text-[10px] text-gray-500">תורים סה"כ</div>
              </div>
              <div class="text-center">
                <div class="text-lg font-bold text-gray-900">{{ lead.business.appointmentsLast30d }}</div>
                <div class="text-[10px] text-gray-500">ב-30 יום</div>
              </div>
              <div class="text-center">
                <div class="text-lg font-bold text-gray-900">{{ lead.business.upcomingAppointments }}</div>
                <div class="text-[10px] text-gray-500">עתידיים</div>
              </div>
              <div class="text-center">
                <div class="text-lg font-bold text-gray-900">{{ lead.business.servicesCount ?? 0 }}</div>
                <div class="text-[10px] text-gray-500">שירותים</div>
              </div>
            </div>
            <div class="flex items-center justify-center flex-wrap gap-2 mt-2 text-[11px] text-gray-500">
              <span>{{ lead.business.plan === 'premium' ? 'פרימיום' : 'חינמי' }}</span>
              <span>·</span>
              <span>{{ contractStatusLabel(lead.business.contractStatus) }}</span>
              <template v-if="lead.business.contractStatus === 'trial' && lead.business.trialEndDate">
                <span>·</span>
                <span>ניסיון עד {{ formatDate(lead.business.trialEndDate) }}</span>
              </template>
              <span>·</span>
              <span>נרשם {{ formatDate(lead.business.createdAt) }}</span>
            </div>
          </RouterLink>

          <Button
            label="אתר העסק"
            icon="pi pi-external-link"
            outlined
            size="small"
            class="w-full !rounded-xl !text-xs"
            @click="openBusinessSite"
          />
        </div>

        <Button
          v-else
          label="קשר לעסק רשום"
          icon="pi pi-link"
          outlined
          size="small"
          class="w-full !rounded-xl"
          @click="showLinkDialog = true"
        />

        <!-- Status + assignment -->
        <div>
          <label class="text-xs font-semibold text-gray-500 block mb-2">סטטוס</label>
          <div class="flex flex-wrap gap-1.5">
            <button
              v-for="s of STAGE_OPTIONS"
              :key="s.value"
              type="button"
              class="flex items-center gap-1 px-3 h-8 rounded-full border text-xs font-medium transition-colors"
              :class="
                lead.stage === s.value
                  ? STAGE_CHIP_CLASSES[s.value] + ' ring-2 ring-offset-1 ring-primary-400'
                  : 'bg-white border-gray-200 text-gray-500'
              "
              @click="updateField('stage', s.value)"
            >
              <i :class="['pi', STAGE_ICONS[s.value], 'text-[10px]']" />
              {{ s.label }}
            </button>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-2">
          <div>
            <label class="text-xs font-semibold text-gray-500 block mb-1.5">אחראי</label>
            <Select
              :modelValue="lead.assignedTo || null"
              :options="assigneeOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="לא משויך"
              class="w-full !text-xs"
              showClear
              @update:modelValue="(v: string) => updateField('assignedTo', v || '')"
            />
          </div>
          <div>
            <label class="text-xs font-semibold text-gray-500 block mb-1.5">עדיפות</label>
            <Select
              :modelValue="lead.priority || 'medium'"
              :options="priorityOptions"
              optionLabel="label"
              optionValue="value"
              class="w-full !text-xs"
              @update:modelValue="(v: PlatformLeadPriorityEnum) => updateField('priority', v)"
            />
          </div>
        </div>

        <!-- Contact details -->
        <div class="bg-gray-50 rounded-xl p-3 space-y-2 text-sm">
          <div class="flex justify-between items-center gap-2">
            <span class="text-gray-400 text-xs flex-shrink-0">איש קשר</span>
            <input
              :value="lead.contactName || ''"
              class="text-end font-medium text-gray-900 bg-transparent border-0 border-b border-transparent hover:border-gray-300 focus:border-primary-400 outline-none px-1 min-w-0 flex-1"
              placeholder="—"
              @change="updateField('contactName', ($event.target as HTMLInputElement).value)"
            />
          </div>
          <div class="flex justify-between items-center gap-2">
            <span class="text-gray-400 text-xs flex-shrink-0">טלפון</span>
            <input
              :value="lead.phone"
              dir="ltr"
              class="text-end font-medium text-gray-900 bg-transparent border-0 border-b border-transparent hover:border-gray-300 focus:border-primary-400 outline-none px-1 min-w-0 flex-1"
              placeholder="—"
              @change="updateField('phone', ($event.target as HTMLInputElement).value)"
            />
          </div>
          <div class="flex justify-between items-center gap-2">
            <span class="text-gray-400 text-xs flex-shrink-0">אימייל</span>
            <input
              :value="lead.email || ''"
              dir="ltr"
              class="text-end font-medium text-gray-900 bg-transparent border-0 border-b border-transparent hover:border-gray-300 focus:border-primary-400 outline-none px-1 min-w-0 flex-1"
              placeholder="—"
              @change="updateField('email', ($event.target as HTMLInputElement).value)"
            />
          </div>
          <div class="flex justify-between items-center">
            <span class="text-gray-400 text-xs">נרשם</span>
            <span class="text-gray-700 text-xs">{{ formatDate(lead.signupDate) }}</span>
          </div>
          <div class="flex justify-between items-center">
            <span class="text-gray-400 text-xs">קשר אחרון</span>
            <span class="text-xs" :class="stale ? 'text-red-600 font-semibold' : 'text-gray-700'">
              {{ relativeDate(lead.lastContactedAt) }}
            </span>
          </div>
        </div>

        <!-- Waiting for -->
        <div>
          <label class="text-xs font-semibold text-gray-500 block mb-1.5">מחכה לפיצ'ר</label>
          <div class="flex flex-wrap gap-1.5 mb-2">
            <Chip
              v-for="(feature, idx) of lead.requestedFeatures"
              :key="idx"
              :label="feature"
              removable
              class="!text-xs"
              @remove="removeFeature(idx)"
            />
            <span v-if="!lead.requestedFeatures?.length" class="text-[11px] text-gray-400">אין</span>
          </div>
          <div class="flex gap-2">
            <InputText v-model="newFeature" placeholder="הוסף פיצ'ר..." class="!text-sm flex-1" @keydown.enter="addFeature" />
            <Button icon="pi pi-plus" size="small" class="!rounded-lg" :disabled="!newFeature.trim()" @click="addFeature" />
          </div>
          <div v-if="featureSuggestions.length" class="flex flex-wrap gap-1 mt-2">
            <button
              v-for="f of featureSuggestions"
              :key="f.feature"
              class="text-[11px] bg-gray-100 text-gray-600 px-2 py-1 rounded-full hover:bg-primary-50 hover:text-primary-700"
              @click="addSuggestedFeature(f.feature)"
            >
              + {{ f.feature }} ({{ f.count }})
            </button>
          </div>
        </div>

        <!-- Tasks -->
        <div>
          <div class="flex items-center justify-between mb-2">
            <label class="text-xs font-semibold text-gray-500">משימות</label>
            <Button
              :icon="showTaskForm ? 'pi pi-times' : 'pi pi-plus'"
              :label="showTaskForm ? 'ביטול' : 'משימה חדשה'"
              outlined
              size="small"
              class="!text-xs !rounded-lg !py-1"
              @click="toggleTaskForm"
            />
          </div>

          <div v-if="showTaskForm" class="bg-primary-50 border border-primary-100 rounded-xl p-3 mb-2 space-y-2">
            <InputText v-model="taskForm.title" placeholder="מה לעשות?" class="!text-sm w-full" />
            <div class="flex gap-2">
              <input v-model="taskForm.dateStr" type="date" class="flex-1 border border-gray-300 rounded-lg px-2 py-1.5 text-xs bg-white" />
              <input v-model="taskForm.timeStr" type="time" class="w-24 border border-gray-300 rounded-lg px-2 py-1.5 text-xs bg-white" />
            </div>
            <Select
              v-model="taskForm.assignedTo"
              :options="assigneeOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="אחראי (לא חובה)"
              class="w-full !text-xs"
              showClear
            />
            <div class="flex flex-wrap gap-1.5">
              <button
                v-for="p of TASK_DATE_PRESETS"
                :key="p.label"
                type="button"
                class="px-2 h-6 rounded-full bg-white border border-primary-200 text-primary-700 text-[11px]"
                @click="taskForm.dateStr = p.date()"
              >
                {{ p.label }}
              </button>
            </div>
            <div class="flex justify-end gap-2">
              <Button label="ביטול" text size="small" class="!text-xs" @click="showTaskForm = false" />
              <Button
                label="הוסף"
                size="small"
                class="!text-xs !bg-purple-600 !border-purple-600 !text-white"
                :loading="savingTask"
                :disabled="!taskForm.title.trim() || !taskForm.dateStr"
                @click="createTask"
              />
            </div>
          </div>

          <div v-if="tasks.length" class="space-y-1.5">
            <div
              v-for="t of tasks"
              :key="t._id"
              class="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2 text-xs"
              :class="{ 'opacity-50': t.completed }"
            >
              <Checkbox :modelValue="t.completed" binary class="!w-4 !h-4" @update:modelValue="(v: boolean) => toggleTask(t, v)" />
              <div class="flex-1 min-w-0">
                <div :class="{ 'line-through': t.completed }">{{ t.title }}</div>
                <div class="text-[10px] text-gray-400">{{ formatDateTime(t.dueDate) }}</div>
              </div>
              <span v-if="t.assignedTo" class="text-[10px] text-gray-500 whitespace-nowrap">
                {{ assigneeLabel(t.assignedTo) }}
              </span>
              <Tag v-if="isTaskOverdue(t)" value="באיחור" severity="danger" class="!text-[9px]" />
            </div>
          </div>
          <div v-else-if="!showTaskForm" class="text-[11px] text-gray-400 text-center py-2">אין משימות</div>
        </div>

        <!-- History -->
        <div>
          <label class="text-xs font-semibold text-gray-500 block mb-2">היסטוריה</label>
          <div class="space-y-0">
            <div v-for="(note, idx) of sortedNotes" :key="idx" class="flex gap-2.5 pb-3">
              <div class="flex-shrink-0 flex flex-col items-center">
                <div
                  class="w-7 h-7 rounded-full flex items-center justify-center"
                  :class="ACTIVITY_TYPE_COLORS[note.type || 'note']"
                >
                  <i :class="['pi', ACTIVITY_TYPE_ICONS[note.type || 'note'], 'text-[10px]']" />
                </div>
                <div v-if="idx < sortedNotes.length - 1" class="w-px flex-1 bg-gray-200 mt-1" />
              </div>
              <div class="flex-1 min-w-0 pt-0.5">
                <div class="flex items-center gap-2 mb-0.5 flex-wrap">
                  <span
                    class="text-[10px] font-medium"
                    :class="note.type === 'system' ? 'text-gray-400 italic' : 'text-gray-700'"
                  >
                    {{ ACTIVITY_TYPE_LABELS[note.type || 'note'] }}
                  </span>
                  <span
                    v-if="note.outcome"
                    class="text-[10px] px-1.5 py-0.5 rounded-full border"
                    :class="OUTCOME_CHIP_CLASSES[note.outcome]"
                  >
                    {{ OUTCOME_LABELS[note.outcome] }}
                  </span>
                  <span class="text-[10px] text-gray-400">{{ formatDateTime(note.createdAt) }}</span>
                </div>
                <div
                  v-if="note.text"
                  class="text-xs text-gray-700 whitespace-pre-wrap"
                  :class="{ 'text-gray-400 italic': note.type === 'system' }"
                >
                  {{ note.text }}
                </div>
              </div>
            </div>
            <div v-if="!lead.notes?.length" class="text-xs text-gray-400 text-center py-3">אין היסטוריה</div>
          </div>
        </div>

        <!-- Test business -->
        <div class="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5">
          <ToggleSwitch :modelValue="!!lead.isTestBusiness" inputId="leadIsTest" @update:modelValue="(v: boolean) => updateField('isTestBusiness', v)" />
          <label for="leadIsTest" class="text-xs text-amber-900 select-none cursor-pointer flex flex-col">
            <span class="font-medium">עסק בדיקה</span>
            <span class="text-[11px] text-amber-700">יוסתר כברירת מחדל</span>
          </label>
        </div>

        <Button label="מחק ליד" icon="pi pi-trash" text severity="danger" size="small" class="w-full" @click="onDelete" />
      </div>
    </div>
  </Drawer>

  <!-- Link business -->
  <Dialog v-model:visible="showLinkDialog" modal header="קישור לעסק רשום" :style="{ width: 'min(400px, 90vw)' }" :draggable="false">
    <div class="flex flex-col gap-3 text-sm">
      <label class="text-gray-600 font-medium">הזן את ה-BID של העסק הרשום</label>
      <InputText v-model="linkBid" placeholder="BID" />
      <div v-if="linkError" class="text-red-500 text-xs">{{ linkError }}</div>
    </div>
    <template #footer>
      <div class="flex justify-end gap-2">
        <Button label="ביטול" text size="small" @click="showLinkDialog = false" />
        <Button label="קשר" size="small" :loading="linking" class="!bg-purple-600 !border-purple-600 !text-white" @click="doLinkBusiness" />
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { DateTime } from 'luxon';
import type {
  IPlatformFeaturePopularity,
  IPlatformLead,
  IPlatformLeadWithStats,
  IPlatformReminder,
} from '@torim-app/types';
import { PlatformLeadPriorityEnum } from '@torim-app/types';
import { CrmApi } from '../../api/crm-api';
import { RemindersApi } from '../../api/reminders-api';
import { businessSiteUrl } from '../../api/impersonation-api';
import { PlatformRouteNames } from '../../router/route-names';
import { useIsMobile } from '../../composables/useIsMobile';
import { useNotify } from '../../composables/useNotify';
import {
  ACTIVITY_TYPE_COLORS,
  ACTIVITY_TYPE_ICONS,
  ACTIVITY_TYPE_LABELS,
  OUTCOME_CHIP_CLASSES,
  OUTCOME_LABELS,
  PRIORITY_LABELS,
  SOURCE_LABELS,
  STAGE_CHIP_CLASSES,
  STAGE_ICONS,
  STAGE_LABELS,
  STAGE_OPTIONS,
  ASSIGNEES,
  assigneeLabel,
  contractStatusLabel,
  daysSinceContact,
  formatDate,
  formatDateTime,
  isLeadStale,
  isTaskOverdue,
  relativeDate,
  toDialable,
  toWhatsappNumber,
} from '../../composables/useLeadDisplay';

const props = defineProps<{
  lead: IPlatformLeadWithStats | null;
  popularFeatures?: IPlatformFeaturePopularity[];
}>();

const emit = defineEmits<{
  (e: 'update', lead: IPlatformLead): void;
  (e: 'delete', lead: IPlatformLead): void;
  (e: 'log', lead: IPlatformLeadWithStats): void;
  (e: 'tasks-changed'): void;
  (e: 'close'): void;
}>();

const { isMobile } = useIsMobile();
const { notifyError, notifySuccess } = useNotify();
const drawerWidth = computed(() => (isMobile.value ? '100vw' : '480px'));

const visible = ref(false);
const newFeature = ref('');
const tasks = ref<IPlatformReminder[]>([]);
const showTaskForm = ref(false);
const savingTask = ref(false);
const taskForm = ref({ title: '', dateStr: '', timeStr: '09:00', assignedTo: '' });

const openBusinessSite = () => {
  const bid = props.lead?.business?.bid || props.lead?.bid;
  if (bid) window.open(businessSiteUrl(bid), '_blank', 'noopener');
};

const isoDaysFromNow = (days: number) =>
  DateTime.local().plus({ days }).toFormat('yyyy-MM-dd');

const TASK_DATE_PRESETS = [
  { label: 'מחר', date: () => isoDaysFromNow(1) },
  { label: 'בעוד 3 ימים', date: () => isoDaysFromNow(3) },
  { label: 'שבוע', date: () => isoDaysFromNow(7) },
  { label: 'חודש', date: () => isoDaysFromNow(30) },
];

/** Opens the form pre-filled: tomorrow 09:00, owned by whoever owns the lead */
const toggleTaskForm = () => {
  showTaskForm.value = !showTaskForm.value;
  if (!showTaskForm.value) return;
  taskForm.value = {
    title: '',
    dateStr: isoDaysFromNow(1),
    timeStr: '09:00',
    assignedTo: props.lead?.assignedTo || '',
  };
};
const showLinkDialog = ref(false);
const linkBid = ref('');
const linkError = ref('');
const linking = ref(false);

watch(
  () => props.lead,
  (val) => {
    visible.value = !!val;
    if (val) {
      fetchTasks();
      showTaskForm.value = false;
      newFeature.value = '';
    }
  },
  { immediate: true },
);

watch(visible, (val) => {
  if (!val) emit('close');
});

const stale = computed(() => (props.lead ? isLeadStale(props.lead) : false));

const staleLabel = computed(() => {
  if (!props.lead) return '';
  const days = daysSinceContact(props.lead);
  return days === null ? 'לא דובר מעולם' : `${days} ימים ללא מגע`;
});

const priorityOptions = Object.values(PlatformLeadPriorityEnum).map((v) => ({
  value: v,
  label: PRIORITY_LABELS[v],
}));

const assigneeOptions = ASSIGNEES;

const featureSuggestions = computed(() => {
  if (!props.popularFeatures?.length) return [];
  const existing = new Set((props.lead?.requestedFeatures || []).map((f) => f.toLowerCase().trim()));
  return props.popularFeatures.filter((f) => !existing.has(f.feature.toLowerCase().trim())).slice(0, 5);
});

const sortedNotes = computed(() => {
  if (!props.lead?.notes) return [];
  return [...props.lead.notes].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
});

const updateField = async (field: string, value: unknown) => {
  if (!props.lead?._id) return;
  if (value === (props.lead as unknown as Record<string, unknown>)[field]) return;
  try {
    const updated = await CrmApi.updateLead(props.lead._id, { [field]: value });
    emit('update', updated);
  } catch (e) {
    console.error(`Failed to update ${field}:`, e);
    notifyError('עדכון הליד נכשל');
  }
};

const updateFeatures = async (features: string[]) => {
  if (!props.lead?._id) return;
  try {
    const updated = await CrmApi.updateLead(props.lead._id, { requestedFeatures: features });
    emit('update', updated);
  } catch (e) {
    console.error('Failed to update features:', e);
    notifyError('עדכון הפיצ\'רים נכשל');
  }
};

const addFeature = async () => {
  if (!newFeature.value.trim()) return;
  await updateFeatures([...(props.lead?.requestedFeatures || []), newFeature.value.trim()]);
  newFeature.value = '';
};

const addSuggestedFeature = (feature: string) =>
  updateFeatures([...(props.lead?.requestedFeatures || []), feature]);

const removeFeature = (idx: number) => {
  const features = [...(props.lead?.requestedFeatures || [])];
  features.splice(idx, 1);
  return updateFeatures(features);
};

const fetchTasks = async () => {
  if (!props.lead?._id) return;
  try {
    tasks.value = await RemindersApi.list({ leadId: props.lead._id });
  } catch {
    tasks.value = [];
  }
};

const createTask = async () => {
  if (!taskForm.value.title.trim() || !taskForm.value.dateStr || !props.lead?._id) return;
  savingTask.value = true;
  try {
    const dueDate = DateTime.fromISO(
      `${taskForm.value.dateStr}T${taskForm.value.timeStr || '09:00'}`,
    ).toISO() as string;
    await RemindersApi.create({
      title: taskForm.value.title.trim(),
      dueDate,
      leadId: props.lead._id,
      leadTitle: props.lead.businessName,
      bid: props.lead.bid || undefined,
      // businessName is what the calendar and task rows render
      businessName: props.lead.business?.title || props.lead.businessName,
      assignedTo: taskForm.value.assignedTo || undefined,
    });
    taskForm.value = { title: '', dateStr: '', timeStr: '09:00', assignedTo: '' };
    showTaskForm.value = false;
    notifySuccess('המשימה נוצרה');
    await fetchTasks();
    emit('tasks-changed');
  } catch (e) {
    console.error('Failed to create task:', e);
    notifyError('יצירת המשימה נכשלה');
  } finally {
    savingTask.value = false;
  }
};

const toggleTask = async (t: IPlatformReminder, completed: boolean) => {
  if (!t._id) return;
  try {
    await RemindersApi.update(t._id, { completed });
    await fetchTasks();
  } catch (e) {
    console.error('Failed to toggle task:', e);
    notifyError('עדכון המשימה נכשל');
  }
};

const doLinkBusiness = async () => {
  if (!props.lead?._id || !linkBid.value.trim()) return;
  linking.value = true;
  linkError.value = '';
  try {
    const updated = await CrmApi.linkBusiness(props.lead._id, linkBid.value.trim());
    showLinkDialog.value = false;
    linkBid.value = '';
    emit('update', updated);
  } catch (e) {
    linkError.value = typeof e === 'string' ? e : 'קישור נכשל';
  } finally {
    linking.value = false;
  }
};

const onDelete = async () => {
  if (!props.lead?._id) return;
  if (!confirm('למחוק את הליד? המשימות שלו יימחקו גם.')) return;
  try {
    await CrmApi.deleteLead(props.lead._id);
    emit('delete', props.lead);
    visible.value = false;
  } catch (e) {
    console.error('Failed to delete lead:', e);
    notifyError('מחיקת הליד נכשלה');
  }
};
</script>
